import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchVariableTypes } from "../../../api/service/settings";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Modern Variable Type Selector Component
 * 
 * Features:
 * - Fast variable type fetching with debounced search
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedVariableTypes - Array of selected variable type objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of variable types)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: true)
 * @param {Array} props.filters - Additional filters to apply
 * @param {boolean} props.showHeader - Show header section (default: true)
 * @param {boolean} props.autoClose - Auto-close after selection when multiple is false (default: true)
 */
export default function ModernVariableTypeSelector({
  open = false,
  selectedVariableTypes = [],
  onChange,
  onClose,
  multiple = true,
  filters = [],
  showHeader = true,
  autoClose = true,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [variableTypes, setVariableTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedVariableTypes.map((vt) => vt?.id).filter(Boolean))
  );

  const perPage = 20;
  const prevSelectedVariableTypesRef = useRef([]);
  const isInternalUpdateRef = useRef(false);

  // Update selectedIds when selectedVariableTypes prop changes (only when dialog opens)
  useEffect(() => {
    if (!open) {
      // Reset internal update flag when dialog closes
      isInternalUpdateRef.current = false;
      return;
    }
    
    if (!isInternalUpdateRef.current) {
      // Only update if the actual IDs have changed (not just reference)
      const prevIds = new Set(prevSelectedVariableTypesRef.current.map((vt) => vt?.id).filter(Boolean));
      const currentIds = new Set(selectedVariableTypes.map((vt) => vt?.id).filter(Boolean));
      
      const prevIdsStr = Array.from(prevIds).sort().join(',');
      const currentIdsStr = Array.from(currentIds).sort().join(',');
      
      if (prevIdsStr !== currentIdsStr) {
        setSelectedIds(currentIds);
        prevSelectedVariableTypesRef.current = selectedVariableTypes.map(vt => ({ ...vt }));
      } else {
        // Even if IDs are the same, update the ref to prevent unnecessary updates
        prevSelectedVariableTypesRef.current = selectedVariableTypes.map(vt => ({ ...vt }));
      }
    }
  }, [selectedVariableTypes, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setShowFilters(false);
    }
  }, [open]);

  // Search function
  const searchFunction = useCallback(async (query, page, filters) => {
    setIsLoading(true);
    setError(null);

    try {
      const controller = {
        query: query || "",
        page: page,
        per_page: perPage,
        sortBy: "created_at",
        sort: "desc",
        filters: filters,
        view: null,
      };

      const response = await fetchVariableTypes(controller);

      if (response && response.data) {
        if (page === 1) {
          setVariableTypes(response.data);
        } else {
          setVariableTypes((prev) => [...prev, ...response.data]);
        }

        setTotalCount(response.total_count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setVariableTypes([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching variable types:", err);
      setError("Kunne ikke laste variable typer");
      setVariableTypes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction changes
  useEffect(() => {
    // Cancel previous debounced calls
    debouncedSearchRef.current.cancel();
    // Create new debounced function
    debouncedSearchRef.current = debounce(searchFunction, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // searchFunction is stable (useCallback with empty deps)

  // Fetch variable types when search query changes or dialog opens
  useEffect(() => {
    if (!open) {
      // Cancel any pending searches when dialog closes
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    // Load variable types automatically when dialog opens (empty query loads all)
    if (searchQuery && searchQuery.trim()) {
      setCurrentPage(1);
      debouncedSearchRef.current(searchQuery, 1, filters);
    } else {
      // Load all variable types when query is empty (initial load)
      setCurrentPage(1);
      debouncedSearchRef.current("", 1, filters);
    }

    return () => {
      debouncedSearchRef.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, open]);

  // Load more variable types
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && searchQuery && searchQuery.trim()) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery, nextPage, filters);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, filters]);

  // Toggle variable type selection
  const toggleVariableType = useCallback(
    (variableType) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(variableType.id)) {
        newSelectedIds.delete(variableType.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(variableType.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected variable types - prioritize variable types from current list (they have full data)
      const selectedFromCurrent = variableTypes
        .filter((vt) => newSelectedIds.has(vt.id))
        .map((vt) => ({ ...vt }));
      const selectedFromPrevious = selectedVariableTypes
        .filter(
          (vt) =>
            newSelectedIds.has(vt.id) &&
            !variableTypes.find((v) => v.id === vt.id)
        )
        .map((vt) => ({ ...vt }));
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      // Mark as internal update to prevent infinite loop
      isInternalUpdateRef.current = true;
      
      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection (only if autoClose is enabled)
      if (!multiple && newSelectedIds.size > 0 && onClose && autoClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      variableTypes,
      selectedVariableTypes,
      multiple,
      onChange,
      onClose,
      autoClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if variable type is selected
  const isSelected = useCallback(
    (variableTypeId) => {
      return selectedIds.has(variableTypeId);
    },
    [selectedIds]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg variable typer" : "Velg variable type"}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 rounded-md transition-colors ${
                  showFilters
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Vis filtre"
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter variable typer..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size}{" "}
              {selectedIds.size === 1
                ? "variable type valgt"
                : "variable typer valgt"}
            </div>
          )}
        </div>
      )}

      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter variable typer..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Variable Type List */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
        {isLoading && variableTypes.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster variable typer...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : variableTypes.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen variable typer funnet"
                  : "Søk etter variable typer for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {variableTypes.slice(0, 20).map((variableType) => {
              const selected = isSelected(variableType.id);
              return (
                <div
                  key={variableType.id}
                  onClick={!multiple ? () => toggleVariableType(variableType) : undefined}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    !multiple ? "cursor-pointer hover:bg-gray-50" : ""
                  } transition-colors ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  }`}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVariableType(variableType);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Variable Type Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {variableType.displayTitle || variableType.title}
                          </p>
                          {variableType.description && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {variableType.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && variableTypes.length >= perPage && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere variable typer
            </button>
          </div>
        )}

        {isLoading && variableTypes.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
}

